/**
 * The water material's uniforms for the water under a roof (waterCover.ts). They start empty, covering
 * nothing, until the world's water sets the covers it has.
 */
import { Vector3, Vector4, type IUniform } from 'three';
import { WATER_COVER, coverMirror, type WaterCover } from '../waterCover';

export interface WaterCoverUniforms {
  /** Each cover's middle on the world's xz plane and its unit direction there. */
  uWaterCoverLine: IUniform<Vector4[]>;
  /** Half its length, half its width, and the reach of the mouths' daylight. */
  uWaterCoverSize: IUniform<Vector3[]>;
  uWaterCoverMirror: IUniform<Vector3[]>;
  uWaterCoverShade: IUniform<number>;
}

export function createWaterCoverUniforms(): WaterCoverUniforms {
  const slots = <T>(make: () => T): T[] => Array.from({ length: WATER_COVER.slots }, make);
  return {
    uWaterCoverLine: { value: slots(() => new Vector4(0, 0, 1, 0)) },
    uWaterCoverSize: { value: slots(() => new Vector3(0, 0, WATER_COVER.mouthM)) },
    uWaterCoverMirror: { value: slots(() => new Vector3()) },
    uWaterCoverShade: { value: WATER_COVER.bodyShade },
  };
}

/** Lays the covers into the slots, in order; the world has no more of them than there are slots. */
export function setWaterCovers(uniforms: WaterCoverUniforms, covers: readonly WaterCover[]): void {
  if (covers.length > WATER_COVER.slots)
    throw new Error(`The water takes ${WATER_COVER.slots} covers and was given ${covers.length}`);
  for (let i = 0; i < WATER_COVER.slots; i++) {
    const cover = covers[i];
    const [dx, dy] = cover ? [cover.to[0] - cover.from[0], cover.to[1] - cover.from[1]] : [1, 0];
    const length = Math.hypot(dx, dy);
    if (!cover || length === 0) {
      uniforms.uWaterCoverLine.value[i]!.set(0, 0, 1, 0);
      uniforms.uWaterCoverSize.value[i]!.set(0, 0, WATER_COVER.mouthM);
      uniforms.uWaterCoverMirror.value[i]!.set(0, 0, 0);
      continue;
    }
    // Layout y runs against the world's z.
    uniforms.uWaterCoverLine.value[i]!.set(
      (cover.from[0] + cover.to[0]) / 2,
      -(cover.from[1] + cover.to[1]) / 2,
      dx / length,
      -dy / length
    );
    uniforms.uWaterCoverSize.value[i]!.set(length / 2, cover.halfWidthM, WATER_COVER.mouthM);
    uniforms.uWaterCoverMirror.value[i]!.set(...coverMirror(cover));
  }
}
